// AreaAutor.js — a área do autor: como estão indo os livros dele, e o que os
// leitores escreveram. Refeita em 11/09/2026 a pedido do dono.
//
// ================= NENHUM NÚMERO DE DINHEIRO =================
//
// A versão anterior abria com "Ganhos totais", repetia o valor em cada livro
// e tinha um aviso de repasse no topo. O dono pediu o contrário: aqui só
// entra o que diz se o livro está sendo LIDO — quanta gente viu a página,
// quanta comprou, quanta leu e quanta avaliou. Receita e repasse continuam no
// painel da editora; esta tela responde "como está indo o meu livro?".
//
// O que sobrou do dinheiro é UM link discreto no rodapé, "Dados de
// recebimento", sem valor nenhum: a configuração do Pagar.me só era alcançável
// pelo aviso que saiu, e sem o link um autor novo não teria como cadastrar a
// conta que recebe o repasse.
//
// ================= DUAS TELAS =================
//
//   LISTA ... os quatro números somados + a grade de capas (só a capa, pedido
//             do dono), com a contagem de comentários sem resposta na quina.
//   LIVRO ... os números daquele livro, a chave do aviso por e-mail e os
//             comentários — cada um com "Responder" e o coração da
//             "Aprovação do Autor".
//
// O servidor reconfere a autoria em TODA chamada (AutorAreaController): o id
// do livro vem da tela, e a tela não é prova de nada.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Stars from "../../produto/paginaProduto/Stars";
import FotoPessoa from "../../ui/FotoPessoa";
import Plaquinhas from "../../ui/Plaquinhas";
import { message } from "../../utils/toast";

const API_URL = process.env.REACT_APP_API;

const MAX_RESPOSTA = 2000;

const cabecalhos = (json = false) => ({
  Accept: "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  ...(json ? { "Content-Type": "application/json" } : {}),
});

const fmtData = (iso) => {
  if (!iso) return "";
  const d = new Date(typeof iso === "string" ? iso.replace(" ", "T") : iso);
  return isNaN(d) ? "" : d.toLocaleDateString("pt-BR");
};

const fmtNumero = (n) => (Number(n) || 0).toLocaleString("pt-BR");

const capaDe = (image) => (image ? `${API_URL}public/${image}` : null);

/** Um comentário tem resposta quando pelo menos UM autor do livro respondeu. */
const temRespostaDeAutor = (a) => (a.respostas || []).some((r) => r.autor);

/** Lê a mensagem de erro do servidor, com uma frase de reserva. */
const erroDe = async (r, reserva) => {
  if (r.status === 401) return "Sua sessão expirou. Entre novamente.";
  if (r.status === 429) return "Muitas ações seguidas. Aguarde um minuto.";
  try {
    const d = await r.json();
    return d?.error || d?.message || reserva;
  } catch {
    return reserva;
  }
};

// ---------------------------------------------------------------------------
// Peças
// ---------------------------------------------------------------------------

const Numero = ({ rotulo, valor, icone, nota }) => (
  <div className="bloco p-4">
    <p className="text-xs text-white/45 flex items-center gap-1.5">
      <i className={"bi " + icone} /> {rotulo}
    </p>
    <p className="text-2xl font-extrabold mt-1">{valor}</p>
    {nota && <p className="text-[11px] text-white/40 mt-0.5">{nota}</p>}
  </div>
);

/**
 * A chave do aviso por e-mail. `role="switch"` para o leitor de tela anunciar
 * "ligado/desligado", e não "botão" — é o estado que importa aqui.
 */
const Interruptor = ({ ligado, ocupado, onAlternar, rotulo }) => (
  <button
    type="button"
    role="switch"
    aria-checked={ligado}
    aria-label={rotulo}
    onClick={onAlternar}
    disabled={ocupado}
    className={
      "foco-laranja relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors " +
      "disabled:opacity-60 " +
      (ligado ? "border-insigne-orange bg-insigne-orange" : "border-line bg-white/10")
    }
  >
    <span
      className={
        "inline-block h-4 w-4 rounded-full bg-paper shadow transition-transform " +
        (ligado ? "translate-x-6" : "translate-x-1")
      }
    />
  </button>
);

/** A capa na grade da lista. O título vai no `title` e no leitor de tela. */
const CapaDoLivro = ({ livro, onAbrir }) => {
  const [quebrou, setQuebrou] = useState(false);
  const src = quebrou ? null : capaDe(livro.image);
  const pendentes = Number(livro.sem_resposta) || 0;
  const rotulo =
    livro.titulo +
    (pendentes > 0 ? ` (${pendentes} ${pendentes === 1 ? "comentário sem resposta" : "comentários sem resposta"})` : "");

  return (
    <button
      type="button"
      onClick={onAbrir}
      title={rotulo}
      aria-label={rotulo}
      className="foco-laranja group relative block w-full"
    >
      <div
        className="aspect-[2/3] w-full overflow-hidden rounded-lg border border-line bg-insigne-navy-deep
                   shadow-[0_6px_18px_rgba(0,0,0,0.35)] transition-transform duration-200
                   group-hover:-translate-y-1 group-hover:border-insigne-orange/60"
      >
        {src ? (
          <img
            src={src}
            alt=""
            loading="lazy"
            onError={() => setQuebrou(true)}
            className="h-full w-full object-cover"
          />
        ) : (
          // Sem capa, o título escrito: uma grade de retângulos iguais não
          // deixaria o autor achar o livro que procura.
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 p-2 text-center">
            <i className="bi bi-book text-2xl text-white/30" />
            <span className="line-clamp-4 text-[11px] font-semibold leading-snug text-white/60">{livro.titulo}</span>
          </div>
        )}
      </div>

      {/* A contagem que traz o autor de volta: comentários que ainda esperam
          uma palavra dele. Na quina, como o contador do carrinho. */}
      {pendentes > 0 && (
        <span
          aria-hidden="true"
          className="absolute -right-2 -top-2 flex h-6 min-w-[24px] items-center justify-center rounded-full
                     bg-insigne-orange px-1.5 text-[11px] font-bold text-paper shadow-md"
        >
          {pendentes}
        </span>
      )}
      {!livro.notificar && (
        <span
          aria-hidden="true"
          title="Aviso por e-mail desligado"
          className="absolute bottom-1.5 right-1.5 flex h-6 w-6 items-center justify-center rounded-full
                     bg-insigne-navy-deep/90 text-[11px] text-white/60"
        >
          <i className="bi bi-bell-slash" />
        </span>
      )}
    </button>
  );
};

// ---------------------------------------------------------------------------
// Um comentário, com as duas ações do autor
// ---------------------------------------------------------------------------

const Comentario = ({ a, aprovando, onAprovar, respondendo, onResponder, onApagarResposta }) => {
  const [aberto, setAberto] = useState(false);
  const [texto, setTexto] = useState("");
  const publicado = a.aprovado !== false;
  const aprovadoPorOutro = a.aprovado_autor && !a.aprovacao_minha;

  const publicar = async () => {
    const t = texto.trim();
    if (t.length < 2) {
      message.error("Escreva a resposta.");
      return;
    }
    const ok = await onResponder(t);
    if (ok) {
      setTexto("");
      setAberto(false);
    }
  };

  return (
    <article
      className={
        "cartao p-4 md:p-5 " +
        (!publicado
          ? "border-line bg-insigne-navy-soft/50 opacity-70"
          : a.aprovado_autor
          ? "border-insigne-navy-light/45 bg-insigne-navy-light/[0.06]"
          : "border-line bg-insigne-navy-soft")
      }
    >
      <div className="flex flex-wrap items-center gap-2.5">
        <FotoPessoa pessoa={a} nome={a.usuario_nome} tamanho="w-9 h-9" className="text-sm" />
        <span className="text-sm font-bold">{a.usuario_nome || "Leitor"}</span>
        <Plaquinhas selo={a.selo_insigne} aprovadoAutor={a.aprovado_autor} />
        {a.compra_verificada && (
          <span className="text-[11px] text-emerald-400/80" title="Comprou este livro na Insigne">
            <i className="bi bi-patch-check" /> Compra verificada
          </span>
        )}
        <span className="ml-auto text-[11px] text-white/35">{fmtData(a.created_at)}</span>
      </div>

      <div className="mt-1.5">
        <Stars value={a.nota} size="text-xs" />
      </div>

      {a.comentario ? (
        <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-white/80">{a.comentario}</p>
      ) : (
        <p className="mt-2 text-sm italic text-white/40">Só a nota, sem texto.</p>
      )}

      {!publicado && (
        <p className="mt-3 flex items-start gap-1.5 text-xs text-amber-300/90">
          <i className="bi bi-eye-slash mt-0.5" />
          Despublicado pela moderação da editora: não aparece na página do livro, e por isso não
          pode ser respondido nem aprovado.
        </p>
      )}

      {/* ===== AS RESPOSTAS, recuadas e com a linha à esquerda =====
          O mesmo desenho da página pública do livro: quem lê precisa ver de
          relance que aquilo é réplica, e não outro comentário. */}
      {(a.respostas || []).length > 0 && (
        <div className="mt-3 space-y-3 border-l-2 border-insigne-orange/30 pl-4">
          {a.respostas.map((r) => (
            <div key={r.id}>
              <div className="flex flex-wrap items-center gap-2">
                <FotoPessoa pessoa={r} nome={r.usuario_nome} tamanho="w-7 h-7" className="text-xs" />
                <span className="text-sm font-bold">{r.minha ? "Você" : r.usuario_nome || "Autor"}</span>
                <Plaquinhas autor={r.autor} />
                <span className="text-xs text-white/40">{fmtData(r.created_at)}</span>
                {r.minha && (
                  <button
                    type="button"
                    onClick={() => onApagarResposta(r.id)}
                    className="ml-auto text-[11px] text-white/30 transition-colors hover:text-red-400"
                    title="Apagar esta resposta"
                  >
                    <i className="bi bi-trash" /> Apagar
                  </button>
                )}
              </div>
              <p className="mt-1.5 whitespace-pre-line text-sm leading-relaxed text-white/75">{r.texto}</p>
            </div>
          ))}
        </div>
      )}

      {publicado && (
        <>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {/* ===== O CORAÇÃO: "Aprovação do Autor" =====
                Curtir dá ao comentário o selo azul "Aprovação do Autor", que
                aparece na página pública do livro. É outra cor de propósito:
                o laranja é o "Selo Insigne" da EDITORA, e os dois dizem coisas
                diferentes. Só quem aprovou desfaz — um coautor não apaga o
                gesto do outro (o servidor recusa).
                No comentário do PRÓPRIO autor não há coração: aprovar a si
                mesmo é auto-endosso, e o servidor também recusa. */}
            {!a.propria && (
            <button
              type="button"
              onClick={onAprovar}
              disabled={aprovando || aprovadoPorOutro}
              aria-pressed={!!a.aprovacao_minha}
              title={
                aprovadoPorOutro
                  ? "Outro autor deste livro já aprovou este comentário"
                  : a.aprovacao_minha
                  ? "Tirar a sua aprovação"
                  : "Aprovar: o comentário ganha o selo Aprovação do Autor na página do livro"
              }
              className={
                "foco-laranja inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold " +
                "transition-colors disabled:cursor-default " +
                (a.aprovado_autor
                  ? "border-insigne-navy-light bg-insigne-navy-light/20 text-insigne-navy-light"
                  : "border-line bg-white/5 text-white/70 hover:border-insigne-navy-light/60 hover:text-insigne-navy-light")
              }
            >
              <i
                className={
                  "bi " + (aprovando ? "bi-arrow-repeat animate-spin" : a.aprovado_autor ? "bi-heart-fill" : "bi-heart")
                }
              />
              {aprovadoPorOutro ? "Aprovado por outro autor" : a.aprovacao_minha ? "Aprovado" : "Aprovar"}
            </button>
            )}

            <button
              type="button"
              onClick={() => setAberto((v) => !v)}
              aria-expanded={aberto}
              className="foco-laranja inline-flex items-center gap-1.5 rounded-lg border border-line bg-white/5
                         px-3 py-1.5 text-xs font-semibold text-white/70 transition-colors
                         hover:border-insigne-orange/60 hover:text-insigne-orange"
            >
              <i className="bi bi-reply" /> {aberto ? "Cancelar" : "Responder"}
            </button>
          </div>

          {aberto && (
            <div className="mt-3 border-l-2 border-insigne-orange/30 pl-4">
              <textarea
                value={texto}
                onChange={(e) => setTexto(e.target.value.slice(0, MAX_RESPOSTA))}
                rows={3}
                autoFocus
                placeholder="Responda como autor do livro…"
                aria-label={`Responder ao comentário de ${a.usuario_nome || "leitor"}`}
                className="w-full resize-y rounded-lg border border-line bg-insigne-navy-deep px-3 py-2.5 text-sm
                           text-white placeholder:text-white/40 outline-none focus:ring-1 focus:ring-insigne-navy-light"
              />
              <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                <p className="text-[11px] text-white/40">
                  A resposta aparece embaixo deste comentário, na página do livro, e o leitor recebe um
                  e-mail avisando (um por comentário a cada 30 minutos). · {texto.length}/{MAX_RESPOSTA}
                </p>
                <button
                  type="button"
                  onClick={publicar}
                  disabled={respondendo || texto.trim().length < 2}
                  className="rounded-lg bg-insigne-orange px-4 py-2 text-xs font-bold text-paper transition-colors
                             hover:bg-insigne-orange-dark disabled:opacity-50"
                >
                  {respondendo ? "Publicando…" : "Publicar resposta"}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </article>
  );
};

// ---------------------------------------------------------------------------
// A tela de um livro
// ---------------------------------------------------------------------------

const FILTROS = [
  { chave: "todos", rotulo: "Todos" },
  { chave: "sem_resposta", rotulo: "Sem resposta" },
  { chave: "aprovados", rotulo: "Aprovados" },
];

const LivroDoAutor = ({ resumo, onVoltar, onMudouLista }) => {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(false);
  const [filtro, setFiltro] = useState("todos");
  const [salvandoAviso, setSalvandoAviso] = useState(false);
  const [aprovandoId, setAprovandoId] = useState(null);
  // UM CONJUNTO, e não um id: o autor pode abrir duas caixas de resposta e
  // publicar as duas. Com um id só, a segunda publicação destravava o botão da
  // primeira ainda em andamento, e o segundo clique criava resposta duplicada.
  const [respondendo, setRespondendo] = useState(() => new Set());

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const r = await fetch(`${API_URL}autor/livros/${resumo.id}/avaliacoes`, { headers: cabecalhos() });
        if (!r.ok) throw new Error(await erroDe(r, "Não foi possível carregar os comentários."));
        const d = await r.json();
        if (vivo) setDados(d);
      } catch (e) {
        if (vivo) setErro(e.message || true);
      }
    })();
    return () => { vivo = false; };
  }, [resumo.id]);

  const avaliacoes = dados?.avaliacoes || [];
  const contagem = useMemo(() => ({
    todos: avaliacoes.length,
    sem_resposta: avaliacoes.filter((a) => a.aprovado !== false && !temRespostaDeAutor(a)).length,
    aprovados: avaliacoes.filter((a) => a.aprovado_autor).length,
  }), [avaliacoes]);

  const visiveis = avaliacoes.filter((a) =>
    filtro === "sem_resposta" ? a.aprovado !== false && !temRespostaDeAutor(a)
      : filtro === "aprovados" ? a.aprovado_autor
      : true
  );

  const trocarComentario = (id, muda) =>
    setDados((d) => (d ? { ...d, avaliacoes: d.avaliacoes.map((a) => (a.id === id ? muda(a) : a)) } : d));

  const alternarAviso = async () => {
    if (!dados || salvandoAviso) return;
    const novo = !dados.livro.notificar;
    setSalvandoAviso(true);
    try {
      const r = await fetch(`${API_URL}autor/livros/${resumo.id}/notificacoes`, {
        method: "PUT",
        headers: cabecalhos(true),
        body: JSON.stringify({ notificar: novo }),
      });
      if (!r.ok) throw new Error(await erroDe(r, "Não foi possível mudar o aviso agora."));
      setDados((d) => ({ ...d, livro: { ...d.livro, notificar: novo } }));
      onMudouLista();
      message.success(novo ? "Você vai receber um e-mail a cada comentário novo neste livro." : "Aviso por e-mail desligado para este livro.");
    } catch (e) {
      message.error(e.message);
    } finally {
      setSalvandoAviso(false);
    }
  };

  const aprovar = async (a) => {
    if (aprovandoId) return;
    setAprovandoId(a.id);
    try {
      const r = await fetch(`${API_URL}autor/avaliacoes/${a.id}/aprovar`, { method: "POST", headers: cabecalhos() });
      if (!r.ok) throw new Error(await erroDe(r, "Não foi possível aprovar agora."));
      const d = await r.json();
      trocarComentario(a.id, (x) => ({
        ...x,
        aprovado_autor: !!d.aprovado_autor,
        aprovacao_minha: !!d.aprovado_autor,
      }));
    } catch (e) {
      message.error(e.message);
    } finally {
      setAprovandoId(null);
    }
  };

  const responder = async (a, texto) => {
    if (respondendo.has(a.id)) return false;
    setRespondendo((s) => new Set(s).add(a.id));
    try {
      const r = await fetch(`${API_URL}avaliacoes/${a.id}/responder`, {
        method: "POST",
        headers: cabecalhos(true),
        body: JSON.stringify({ texto }),
      });
      if (!r.ok) throw new Error(await erroDe(r, "Não foi possível publicar a resposta."));
      const d = await r.json();
      const resposta = { ...(d.resposta || {}), texto: d.resposta?.texto ?? texto, autor: true, minha: true };
      trocarComentario(a.id, (x) => ({ ...x, respostas: [...(x.respostas || []), resposta] }));
      onMudouLista();
      // "Avisado" só quando um e-mail vai mesmo sair — o servidor diz
      // (`leitor_avisado`): resposta ao próprio comentário, leitor sem e-mail
      // ou um aviso nos últimos 30 minutos não geram outro.
      message.success(
        d.leitor_avisado ? "Resposta publicada. O leitor vai receber um e-mail avisando." : "Resposta publicada."
      );
      return true;
    } catch (e) {
      message.error(e.message);
      return false;
    } finally {
      setRespondendo((s) => {
        const novo = new Set(s);
        novo.delete(a.id);
        return novo;
      });
    }
  };

  const apagarResposta = async (avaliacaoId, respostaId) => {
    if (!window.confirm("Apagar esta resposta? Ela some da página do livro.")) return;
    try {
      const r = await fetch(`${API_URL}avaliacoes/respostas/${respostaId}`, { method: "DELETE", headers: cabecalhos() });
      if (!r.ok) throw new Error(await erroDe(r, "Não foi possível apagar agora."));
      trocarComentario(avaliacaoId, (x) => ({ ...x, respostas: (x.respostas || []).filter((y) => y.id !== respostaId) }));
      onMudouLista();
    } catch (e) {
      message.error(e.message);
    }
  };

  const livro = dados?.livro || resumo;
  const capa = capaDe(livro.image);

  return (
    <div>
      <button
        type="button"
        onClick={onVoltar}
        className="mb-5 inline-flex items-center gap-2 text-sm text-white/60 transition-colors hover:text-white"
      >
        <i className="bi bi-arrow-left" /> Voltar para meus livros
      </button>

      {/* ===== O LIVRO, em uma faixa: capa, título, os quatro números ===== */}
      <div className="mb-5 flex gap-4">
        <div className="flex h-28 w-20 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-insigne-navy-deep">
          {capa ? (
            <img src={capa} alt="" className="h-full w-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
          ) : (
            <i className="bi bi-book text-xl text-white/30" />
          )}
        </div>
        <div className="min-w-0">
          <h2 className="text-lg font-bold leading-tight md:text-xl">{livro.titulo}</h2>
          <p className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/55">
            <span title="Visitas na página do livro"><i className="bi bi-eye" /> {fmtNumero(resumo.views)} visitas</span>
            <span title="Exemplares comprados"><i className="bi bi-bag-check" /> {fmtNumero(resumo.compras)} compras</span>
            <span title="Pessoas que abriram o livro no leitor"><i className="bi bi-book" /> {fmtNumero(resumo.leitores)} leitores</span>
            <span title="Avaliações publicadas">
              <i className="bi bi-star" /> {fmtNumero(resumo.avaliacoes)} avaliações
              {resumo.avaliacoes > 0 && ` · nota ${Number(resumo.nota_media || 0).toFixed(1).replace(".", ",")}`}
            </span>
          </p>
        </div>
      </div>

      {/* ===== O AVISO POR E-MAIL, por livro =====
          Nasce ligado (a primeira opinião é a que o autor mais quer saber).
          Quem tem dez títulos desliga um sem perder os outros nove. */}
      {dados && (
        <div className="mb-6 flex items-center justify-between gap-4 bloco p-4">
          <div className="min-w-0">
            <p className="flex items-center gap-2 text-sm font-semibold">
              <i className={"bi " + (dados.livro.notificar ? "bi-bell-fill text-insigne-orange" : "bi-bell-slash text-white/40")} />
              Avisar por e-mail a cada comentário novo
            </p>
            <p className="mt-0.5 text-xs text-white/45">
              Vale só para este livro. {dados.livro.notificar ? "Ligado." : "Desligado. Os comentários continuam aparecendo aqui."}
            </p>
          </div>
          <Interruptor
            ligado={!!dados.livro.notificar}
            ocupado={salvandoAviso}
            onAlternar={alternarAviso}
            rotulo="Avisar por e-mail a cada comentário novo neste livro"
          />
        </div>
      )}

      <h3 className="mb-3 flex items-center gap-2 text-lg font-bold">
        <i className="bi bi-chat-quote" /> Comentários dos leitores
      </h3>

      {erro ? (
        <p className="py-10 text-center text-white/50">{typeof erro === "string" ? erro : "Não foi possível carregar os comentários."}</p>
      ) : !dados ? (
        <p className="py-10 text-center text-white/50">Carregando os comentários...</p>
      ) : avaliacoes.length === 0 ? (
        <p className="text-sm leading-relaxed text-white/45">
          Este livro ainda não tem comentários. Quem compra recebe um convite por e-mail para avaliar
          alguns dias depois da compra{dados.livro.notificar ? ", e você é avisado quando o primeiro chegar." : "."}
        </p>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap gap-1.5">
            {FILTROS.filter((f) => f.chave === "todos" || contagem[f.chave] > 0).map((f) => (
              <button
                key={f.chave}
                type="button"
                onClick={() => setFiltro(f.chave)}
                aria-pressed={filtro === f.chave}
                className={
                  "foco-laranja rounded-controle border px-3 py-1 text-xs font-semibold transition-colors " +
                  (filtro === f.chave
                    ? "border-insigne-orange bg-insigne-orange text-paper"
                    : "border-line bg-white/5 text-white/70 hover:bg-white/10 hover:text-white")
                }
              >
                {f.rotulo} <span className={filtro === f.chave ? "text-paper/70" : "text-white/35"}>{contagem[f.chave]}</span>
              </button>
            ))}
          </div>

          {visiveis.length === 0 ? (
            <p className="py-8 text-center text-sm text-white/45">
              {filtro === "sem_resposta" ? "Todos os comentários já têm resposta sua." : "Nenhum comentário aqui."}
            </p>
          ) : (
            <div className="space-y-4">
              {visiveis.map((a) => (
                <Comentario
                  key={a.id}
                  a={a}
                  aprovando={aprovandoId === a.id}
                  onAprovar={() => aprovar(a)}
                  respondendo={respondendo.has(a.id)}
                  onResponder={(texto) => responder(a, texto)}
                  onApagarResposta={(respostaId) => apagarResposta(a.id, respostaId)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// A lista
// ---------------------------------------------------------------------------

const AreaAutor = ({ onConfigurarPagarme }) => {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(false);
  const [aberto, setAberto] = useState(null); // o resumo do livro aberto
  const [versao, setVersao] = useState(0); // recarrega a lista ao voltar

  // Duas travas para as recargas em segundo plano (responder, aprovar, mudar o
  // aviso pedem uma cada):
  //  - `pedido` numera as chamadas, e a resposta que chega fora de ordem é
  //    descartada. Sem isso, desligar e religar o aviso podia terminar com a
  //    resposta ANTIGA por cima, e a capa mostrava o sininho errado;
  //  - `temDados` diz se já existe lista na tela. Uma recarga que falha não
  //    pode trocar a lista inteira por uma mensagem de erro — o autor perdia
  //    o que estava vendo por causa de uma falha de rede momentanea.
  const pedido = useRef(0);
  const temDados = useRef(false);

  const carregar = useCallback(async () => {
    const meu = ++pedido.current;
    try {
      const r = await fetch(`${API_URL}autor/livros`, { headers: cabecalhos() });
      if (!r.ok) throw new Error(await erroDe(r, "Não foi possível carregar seus livros agora."));
      const d = await r.json();
      if (meu !== pedido.current) return;
      setDados(d);
      temDados.current = true;
      setErro(false);
    } catch (e) {
      if (meu !== pedido.current) return;
      if (temDados.current) {
        message.error(e.message || "Não foi possível atualizar a lista agora.");
        return;
      }
      setErro(e.message || true);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar, versao]);

  const voltar = () => {
    setAberto(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (aberto) {
    return (
      <LivroDoAutor
        key={aberto.id}
        resumo={aberto}
        onVoltar={voltar}
        // Responder, apagar ou mudar o aviso altera os números da capa (o
        // contador de sem resposta, o sininho). A lista é relida ao voltar.
        onMudouLista={() => setVersao((v) => v + 1)}
      />
    );
  }

  const livros = dados?.livros || [];
  const totais = dados?.totais || {};
  const semResposta = Number(totais.sem_resposta) || 0;

  return (
    <div>
      <h2 className="mb-5 flex items-center gap-3 titulo-display text-[2.1rem] md:text-[2.5rem]">
        <i className="bi bi-journal-bookmark" /> Área do Autor
      </h2>

      {erro ? (
        <p className="py-10 text-center text-white/50">{typeof erro === "string" ? erro : "Não foi possível carregar seus livros agora."}</p>
      ) : !dados ? (
        <p className="py-10 text-center text-white/50">Carregando seus livros...</p>
      ) : (
        <>
          {/* ===== OS QUATRO NÚMEROS, somados em todos os livros ===== */}
          <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4 orfa-ao-centro-ate-lg">
            <Numero rotulo="Visitas nas páginas" valor={fmtNumero(totais.views)} icone="bi-eye" />
            <Numero rotulo="Compras" valor={fmtNumero(totais.compras)} icone="bi-bag-check" />
            <Numero rotulo="Leitores" valor={fmtNumero(totais.leitores)} icone="bi-book" nota="abriram no leitor" />
            <Numero rotulo="Avaliações" valor={fmtNumero(totais.avaliacoes)} icone="bi-star" />
          </div>

          {semResposta > 0 && (
            <p className="mb-5 flex items-center gap-2 rounded-xl border border-insigne-orange/40 bg-insigne-orange/[0.07] px-4 py-3 text-sm">
              <i className="bi bi-chat-dots-fill text-insigne-orange" />
              <span>
                <strong className="text-insigne-orange">{semResposta}</strong>{" "}
                {semResposta === 1 ? "comentário espera" : "comentários esperam"} sua resposta. O número
                na capa mostra em qual livro.
              </span>
            </p>
          )}

          <div className="mb-3 flex items-baseline justify-between gap-3">
            <h3 className="text-lg font-bold">Meus livros</h3>
            {livros.length > 0 && (
              <span className="text-xs text-white/40">
                {livros.length} {livros.length === 1 ? "publicado" : "publicados"} · clique na capa para ver os comentários
              </span>
            )}
          </div>

          {livros.length === 0 ? (
            <p className="text-sm leading-relaxed text-white/45">
              Você ainda não tem livros vinculados à sua conta. Se você publicou com a editora e não vê seu
              livro aqui, fale com a equipe.
            </p>
          ) : (
            <div className="grid grid-cols-3 gap-x-4 gap-y-5 sm:grid-cols-4 lg:grid-cols-6">
              {livros.map((l) => (
                <CapaDoLivro
                  key={l.id}
                  livro={l}
                  onAbrir={() => {
                    setAberto(l);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                />
              ))}
            </div>
          )}

          {onConfigurarPagarme && (
            <p className="mt-10 border-t border-line pt-4 text-xs text-white/40">
              <button type="button" onClick={onConfigurarPagarme} className="link-sublinhado inline-flex items-center gap-1.5">
                <i className="bi bi-gear" /> Dados de recebimento
              </button>
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default AreaAutor;
